// 신호 대상 선택 — **무선택이 기본이라는 것은 코드 한 줄이 아니라 설계다**
//
// ── 이 모듈이 무엇을 모르는가가 이 모듈의 계약이다 ──────────────────────────
//
// 여기에는 **접속자의 게임 ID 를 받는 문이 없다.** 없어서 못 쓰는 것이지 안 쓰기로 한 것이
// 아니다. 시스템이 접속 ID 로 신호 대상을 골라 주면, 게임 계정을 여럿 굴리는 사람에게는
// 접속에 쓴 ID 와 이번 작전에서 맡은 자리가 달라 그 선택이 **틀린 주장**이 된다. 그러면
// 그 사람은 자기 것을 보고 있다고 믿으면서 틀린 시각에 버튼을 누른다 — **틀린 주장은 아무
// 주장도 하지 않는 것보다 나쁘다.**
//
// 편의를 더하려는 수정이 이것을 조용히 되돌릴 수 있는 종류라, 재료를 아예 두지 않는 쪽을
// 골랐다. 검사는 잘못을 잡지만 재료가 없는 구조는 잘못을 **표현 불가능하게** 만든다.
// 단위 검사 하나가 이 파일의 export 목록을 통째로 대조하므로, 누가 게임 ID 를 받는 문을
// 새로 열면 그 순간 빨개진다.
//
// ── 왜 rally 가 선택 단위인가 ───────────────────────────────────────────────
//
// 같은 roster 가 rally 를 둘 맡을 수 있다. 사람으로 고르게 하면 그 둘이 한 항목으로 합쳐져
// 어느 rally 의 개시를 듣고 있는지가 화면에서 사라진다. 그 둘은 개시 시각이 서로 다르다.
//
// ── 소리와 눈이 갈린다 ──────────────────────────────────────────────────────
//
// 소리는 **정확히 하나**만 난다. 둘이 겹치면 어느 쪽에 반응할지 알 수 없고, 그 혼동이
// 정확히 이 제품이 없애려는 실패다. 반면 눈으로 따라가고 싶은 줄은 여럿일 수 있다 — 자기
// 앞뒤 순번을 함께 보는 것이 실제 쓰임이다. 그래서 상태가 둘로 갈린다. 한 값으로 합치면
// 「여럿 보고 싶다」가 곧 「소리 여럿」이 되어 계약이 깨진다.

/**
 * 이 브라우저의 선택. **서버로 올라가지 않는다** — 남의 화면을 바꾸지 못한다는 것이
 * 혼동 방지 규칙 넷 중 하나이고, 올리지 않으면 그 규칙이 지켜지는 것이 아니라 깨질 수 없다.
 */
export interface SelectionState {
  /** 소리를 낼 rally. **초기값은 언제나 `null` 이다.** */
  readonly signalRallyId: string | null
  /** 눈으로 따라갈 rally 들. 소리와 무관하고 여럿일 수 있다. */
  readonly highlighted: readonly string[]
}

/**
 * 처음 열었을 때의 값. **화면은 이 값으로 시작하고 다른 초기값을 만들지 않는다.**
 *
 * 상수로 두는 이유는 「아무것도 안 고른 상태」를 화면마다 따로 적으면 그중 하나가 언젠가
 * 「하나쯤 골라 두면 편하지 않을까」가 되기 때문이다.
 */
export const NO_SELECTION: SelectionState = { signalRallyId: null, highlighted: [] }

/**
 * 소리 대상을 고르거나 끈다. 이미 고른 줄을 다시 누르면 **꺼진다.**
 *
 * 끄는 문을 따로 만들지 않은 이유: 소리를 멈추고 싶은 사람이 찾는 자리는 지금 울리고 있는
 * 그 줄이지 화면 어딘가의 다른 버튼이 아니다. 그리고 무선택으로 돌아가면 카운트다운도 함께
 * 사라진다 — 이름 없는 카운트다운은 화면에 존재하지 않는다는 계약이 그렇게 만든다.
 */
export function toggleSignal(state: SelectionState, rallyId: string): SelectionState {
  return {
    ...state,
    signalRallyId: state.signalRallyId === rallyId ? null : rallyId,
  }
}

/** 눈으로 따라갈 줄을 더하거나 뺀다. 소리는 건드리지 않는다. */
export function toggleHighlight(state: SelectionState, rallyId: string): SelectionState {
  const on = state.highlighted.includes(rallyId)
  return {
    ...state,
    highlighted: on
      ? state.highlighted.filter((id) => id !== rallyId)
      : [...state.highlighted, rallyId],
  }
}

/**
 * 지금 시퀀스에 없는 줄을 선택에서 걷어낸다.
 *
 * **CT 는 이 사람의 선택을 모른 채 작전을 접거나 줄을 뺀다.** 걷어내지 않으면 사라진 rally
 * 의 카운트다운이 화면에 남고, 그 숫자는 아무것도 가리키지 않으면서 그럴듯하게 흐른다.
 * 이 제품에서 가장 나쁜 종류의 화면이 그것이다 — 틀렸다는 것이 화면에 안 보인다.
 *
 * 화면이 시퀀스를 새로 받을 때마다 부른다. 선택을 저장해 두고 「없으면 무시」로 넘기면,
 * 그 rally 가 다시 생겼을 때 사람이 고른 적 없는 소리가 되살아난다.
 */
export function pruneMissing(
  state: SelectionState,
  presentRallyIds: readonly string[],
): SelectionState {
  const present = new Set(presentRallyIds)
  const signalRallyId =
    state.signalRallyId !== null && present.has(state.signalRallyId) ? state.signalRallyId : null
  const highlighted = state.highlighted.filter((id) => present.has(id))

  // 바뀐 것이 없으면 **같은 객체를 돌려준다.** 새 객체를 만들면 화면이 매 방송마다 다시
  // 그려지고, 그 다시 그리기가 소리 예약을 건드리는 자리까지 흔든다.
  if (signalRallyId === state.signalRallyId && highlighted.length === state.highlighted.length) {
    return state
  }
  return { signalRallyId, highlighted }
}
